import { CloseRounded } from "@mui/icons-material";
import { Box, Button, ButtonBase, IconButton, useMediaQuery } from "@mui/material";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  STICKY_BAR_HEIGHT_VAR,
  STICKY_BAR_OVERLAY_POLICY,
  STICKY_BAR_OVERLAY_TOP_INSET_VAR,
  STICKY_BAR_TOP_INSET_VAR,
  STICKY_BAR_TRANSITION_MS,
} from "../components/stickyBar/stickyBarLayout";

const StickyBarContext = createContext(null);
const DEFAULT_SCOPE = "global";
const DEFAULT_CLOSE_LABEL = "Close";
const ANIMATION_MS = STICKY_BAR_TRANSITION_MS;
const DESKTOP_BREAKPOINT = "md";
const DESKTOP_MEDIA_QUERY = "(min-width:900px)";
const DEFAULT_DISMISSIBLE_LEADING_GUTTER = 12;
const DEFAULT_STICKY_BAR_MIN_HEIGHT = 44;
const DEFAULT_CLOSE_TARGET_WIDTH = 44;
const PROXIED_CALLBACK_KEYS = [
  "renderContent",
  "onOpenChange",
  "onOpen",
  "onVisible",
  "onClose",
  "onDismiss",
  "onHeightChange",
];
const INTERACTIVE_ACTION_SELECTOR = [
  "a",
  "button",
  "input",
  "select",
  "textarea",
  "[role='button']",
  "[data-sticky-bar-ignore-action='true']",
].join(",");

const isControlled = (registration = {}) =>
  Object.prototype.hasOwnProperty.call(registration, "open");

const normalizeRegistration = (config = {}) => {
  const id = String(config?.id || "").trim();
  if (!id) return null;

  return {
    ...config,
    id,
    scope: config.scope || DEFAULT_SCOPE,
    priority: Number.isFinite(Number(config.priority))
      ? Number(config.priority)
      : 0,
    enabled: config.enabled !== false,
    delayMs: Math.max(0, Number(config.delayMs || 0)),
    overlayPolicy:
      config.overlayPolicy === STICKY_BAR_OVERLAY_POLICY.persistentOverlays
        ? STICKY_BAR_OVERLAY_POLICY.persistentOverlays
        : STICKY_BAR_OVERLAY_POLICY.rootOnly,
    dismissible: config.dismissible !== false,
    closeLabel: config.closeLabel || DEFAULT_CLOSE_LABEL,
    layout: config.layout || {},
    registeredAt: Date.now(),
  };
};

const setRootCssVar = (name, value) => {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(name, value);
};

const resetRootCssVars = () => {
  setRootCssVar(STICKY_BAR_HEIGHT_VAR, "0px");
  setRootCssVar(STICKY_BAR_TOP_INSET_VAR, "0px");
  setRootCssVar(STICKY_BAR_OVERLAY_TOP_INSET_VAR, "0px");
};

const stringifyLayoutSignature = (layout = {}) => {
  try {
    return JSON.stringify(layout || {});
  } catch (error) {
    return "unserializable-layout";
  }
};

const getRegistrationSignature = (config = null) => {
  if (!config?.id) return "";

  return JSON.stringify({
    id: config.id,
    scope: config.scope || DEFAULT_SCOPE,
    priority: config.priority ?? 0,
    enabled: config.enabled !== false,
    hasControlledOpen: Object.prototype.hasOwnProperty.call(config, "open"),
    open: config.open,
    defaultOpen: config.defaultOpen,
    delayMs: config.delayMs || 0,
    overlayPolicy: config.overlayPolicy || STICKY_BAR_OVERLAY_POLICY.rootOnly,
    dismissible: config.dismissible !== false,
    closeLabel: config.closeLabel || DEFAULT_CLOSE_LABEL,
    contentKey: config.contentKey ?? null,
    actionKey: config.actionKey ?? null,
    action: config.action
      ? {
          label: config.action.label || "",
          ariaLabel: config.action.ariaLabel || "",
          href: config.action.href || "",
          target: config.action.target || "",
          rel: config.action.rel || "",
          disabled: config.action.disabled === true,
          showButton: config.action.showButton !== false,
          contentButton: config.action.contentButton === true,
          showOnMobile: config.action.showOnMobile !== false,
          showOnDesktop: config.action.showOnDesktop !== false,
          variant: config.action.variant || "contained",
          color: config.action.color || "primary",
        }
      : null,
    layout: stringifyLayoutSignature(config.layout),
  });
};

const buildRegistrationFromRef = (configRef) => {
  const latestConfig = configRef.current || {};
  const registration = { ...latestConfig };

  PROXIED_CALLBACK_KEYS.forEach((key) => {
    if (typeof latestConfig[key] !== "function") return;
    registration[key] = (...args) => configRef.current?.[key]?.(...args);
  });

  if (typeof latestConfig.action?.onClick === "function") {
    registration.action = {
      ...latestConfig.action,
      onClick: (...args) => configRef.current?.action?.onClick?.(...args),
    };
  }

  return registration;
};

const resolveOpenState = ({ registration, internalOpen, delayReady }) => {
  if (!registration?.enabled) return false;
  if (delayReady !== true) return false;
  if (isControlled(registration)) return Boolean(registration.open);
  return Boolean(internalOpen);
};

const getResponsiveWrapValue = (value, fallback = true) => {
  if (typeof value === "boolean") return value;
  if (!value || typeof value !== "object") return fallback;
  return {
    xs: value.mobile === false ? "nowrap" : "normal",
    [DESKTOP_BREAKPOINT]: value.desktop === false ? "nowrap" : "normal",
  };
};

const getContentWrapSx = (layout = {}) => {
  const wrapConfig = layout.allowWrap ?? layout.wrap;
  if (wrapConfig === undefined) return {};
  if (typeof wrapConfig === "boolean") {
    return {
      whiteSpace: wrapConfig ? "normal" : "nowrap",
    };
  }
  return {
    whiteSpace: getResponsiveWrapValue(wrapConfig),
  };
};

const getCssLength = (value, fallback) => {
  const resolvedValue = value ?? fallback;
  if (typeof resolvedValue === "number") {
    return `${Math.max(0, resolvedValue)}px`;
  }
  if (resolvedValue && typeof resolvedValue === "object") {
    return `${fallback}px`;
  }

  const text = String(resolvedValue || "").trim();
  return text || `${fallback}px`;
};

const getCssLengthValuePx = (value, fallback) => {
  const length = getCssLength(value, fallback);
  const match = /^([0-9]+(?:\.[0-9]+)?)px$/u.exec(length);
  return match ? Number(match[1]) : fallback;
};

const shouldConstrainWrappedContent = (layout = {}) => {
  const wrapConfig = layout.allowWrap ?? layout.wrap;
  if (wrapConfig === false) return true;
  if (!wrapConfig || typeof wrapConfig !== "object") return false;
  return wrapConfig.mobile === false || wrapConfig.desktop === false;
};

const hasRegistrationAction = (registration) =>
  Boolean(
    registration?.action &&
    !registration.action.disabled &&
    (typeof registration.action.onClick === "function" ||
      registration.action.href),
  );

const shouldIgnoreBarActionEvent = (event) => {
  const target = event?.target;
  if (!target || typeof target.closest !== "function") return false;
  const interactiveAncestor = target.closest(INTERACTIVE_ACTION_SELECTOR);
  return Boolean(
    interactiveAncestor && interactiveAncestor !== event.currentTarget,
  );
};

const getKeyboardOnlyActionButtonSx = (minHeight) => ({
  position: "absolute",
  left: { xs: 8, sm: 16 },
  top: 0,
  zIndex: 1,
  minHeight: 0,
  height: minHeight,
  maxHeight: "100%",
  minWidth: 44,
  maxWidth: "calc(100% - 64px)",
  opacity: 0,
  pointerEvents: "none",
  textTransform: "none",
  borderRadius: 1,
  transform: "translateY(-4px)",
  transition: "opacity 120ms ease, transform 120ms ease",
  "&:focus-visible": {
    opacity: 1,
    pointerEvents: "auto",
    transform: "none",
  },
});

const selectActiveRegistration = ({
  registrations = {},
  internalOpenById = {},
  delayReadyById = {},
  scope = DEFAULT_SCOPE,
} = {}) =>
  Object.values(registrations)
    .filter((registration) => registration?.scope === scope)
    .filter((registration) =>
      resolveOpenState({
        registration,
        internalOpen: internalOpenById[registration.id],
        delayReady: delayReadyById[registration.id],
      }),
    )
    .sort((left, right) => {
      if (right.priority !== left.priority)
        return right.priority - left.priority;
      return right.registeredAt - left.registeredAt;
    })[0] || null;

const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(Boolean(media.matches));
    update();

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }

    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  return prefersReducedMotion;
};

export function StickyBarProvider({ children }) {
  const [suppressions, setSuppressions] = useState(() => new Set());
  const [registrations, setRegistrations] = useState({});
  const [internalOpenById, setInternalOpenById] = useState({});
  const [delayReadyById, setDelayReadyById] = useState({});
  const registrationsRef = useRef(registrations);
  const timersRef = useRef({});

  const suppressStickyBar = useCallback(() => {
    const token = Symbol("sticky-bar-suppression");
    setSuppressions((current) => new Set(current).add(token));
    return () => setSuppressions((current) => {
      if (!current.has(token)) return current;
      const next = new Set(current);
      next.delete(token);
      return next;
    });
  }, []);

  useEffect(() => {
    registrationsRef.current = registrations;
  }, [registrations]);

  const unregisterStickyBar = useCallback((id) => {
    const key = String(id || "").trim();
    if (!key) return;

    if (timersRef.current[key]) {
      window.clearTimeout(timersRef.current[key]);
      delete timersRef.current[key];
    }

    setRegistrations((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setInternalOpenById((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setDelayReadyById((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const registerStickyBar = useCallback(
    (config = {}) => {
      const registration = normalizeRegistration(config);
      if (!registration) {
        return () => {};
      }
      const previousRegistration = registrationsRef.current[registration.id];
      const delayChanged =
        previousRegistration &&
        previousRegistration.delayMs !== registration.delayMs;

      if (delayChanged && timersRef.current[registration.id]) {
        window.clearTimeout(timersRef.current[registration.id]);
        delete timersRef.current[registration.id];
      }

      setRegistrations((current) => ({
        ...current,
        [registration.id]: {
          ...current[registration.id],
          ...registration,
          registeredAt:
            current[registration.id]?.registeredAt || registration.registeredAt,
        },
      }));

      setDelayReadyById((current) => {
        if (!delayChanged && current[registration.id] !== undefined) {
          return current;
        }
        return {
          ...current,
          [registration.id]: registration.delayMs <= 0,
        };
      });

      setInternalOpenById((current) => {
        if (
          isControlled(registration) ||
          (!delayChanged && current[registration.id] !== undefined)
        ) {
          return current;
        }

        return {
          ...current,
          [registration.id]:
            registration.delayMs > 0
              ? false
              : Boolean(registration.defaultOpen),
        };
      });

      return () => unregisterStickyBar(registration.id);
    },
    [unregisterStickyBar],
  );

  useEffect(() => {
    Object.values(registrations).forEach((registration) => {
      const id = registration.id;
      if (!registration.enabled) return;

      if (registration.delayMs <= 0) {
        if (delayReadyById[id] !== true) {
          setDelayReadyById((current) => ({ ...current, [id]: true }));
        }
        return;
      }

      if (delayReadyById[id] === true || timersRef.current[id]) return;

      timersRef.current[id] = window.setTimeout(() => {
        delete timersRef.current[id];
        setDelayReadyById((current) => ({ ...current, [id]: true }));
        setInternalOpenById((current) => {
          const latest = registrationsRef.current[id];
          if (!latest || isControlled(latest) || current[id] === true)
            return current;
          return {
            ...current,
            [id]: Boolean(latest.defaultOpen),
          };
        });
      }, registration.delayMs);
    });

    Object.keys(timersRef.current).forEach((id) => {
      if (registrations[id]) return;
      window.clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    });
  }, [delayReadyById, registrations]);

  useEffect(
    () => () => {
      Object.values(timersRef.current).forEach((timerId) =>
        window.clearTimeout(timerId),
      );
      timersRef.current = {};
    },
    [],
  );

  const setRegistrationOpen = useCallback((id, nextOpen, reason = "manual") => {
    const key = String(id || "").trim();
    const registration = registrationsRef.current[key];
    if (!registration) return;

    if (isControlled(registration)) {
      registration.onOpenChange?.(Boolean(nextOpen), { id: key, reason });
    } else {
      setInternalOpenById((current) => ({
        ...current,
        [key]: Boolean(nextOpen),
      }));
    }

    if (nextOpen) {
      registration.onOpen?.({ id: key, reason });
      return;
    }

    registration.onClose?.({ id: key, reason });
    if (reason === "dismiss") {
      registration.onDismiss?.({ id: key, reason });
    }
  }, []);

  const openStickyBar = useCallback(
    (id) => setRegistrationOpen(id, true, "manual"),
    [setRegistrationOpen],
  );

  const closeStickyBar = useCallback(
    (id, reason = "manual") => setRegistrationOpen(id, false, reason),
    [setRegistrationOpen],
  );

  const clearStickyBar = useCallback(
    (id) => unregisterStickyBar(id),
    [unregisterStickyBar],
  );

  const getStickyBarState = useCallback(
    (id) => {
      const key = String(id || "").trim();
      const registration = registrations[key] || null;
      if (!registration) {
        return {
          id: key,
          registered: false,
          open: false,
          active: false,
        };
      }

      const activeRegistration = selectActiveRegistration({
        registrations,
        internalOpenById,
        delayReadyById,
        scope: registration.scope,
      });

      return {
        id: key,
        registered: true,
        open: resolveOpenState({
          registration,
          internalOpen: internalOpenById[key],
          delayReady: delayReadyById[key],
        }),
        active: activeRegistration?.id === key,
        delayReady: delayReadyById[key] === true,
        registration,
      };
    },
    [delayReadyById, internalOpenById, registrations],
  );

  const getActiveStickyBar = useCallback(
    (scope = DEFAULT_SCOPE) =>
      selectActiveRegistration({
        registrations,
        internalOpenById,
        delayReadyById,
        scope,
      }),
    [delayReadyById, internalOpenById, registrations],
  );

  const contextValue = useMemo(
    () => ({
      registrations,
      registerStickyBar,
      unregisterStickyBar,
      openStickyBar,
      closeStickyBar,
      clearStickyBar,
      getStickyBarState,
      getActiveStickyBar,
      suppressStickyBar,
      isSuppressed: suppressions.size > 0,
    }),
    [
      clearStickyBar,
      closeStickyBar,
      getActiveStickyBar,
      getStickyBarState,
      openStickyBar,
      registerStickyBar,
      registrations,
      suppressStickyBar,
      suppressions,
      unregisterStickyBar,
    ],
  );

  return (
    <StickyBarContext.Provider value={contextValue}>
      {children}
    </StickyBarContext.Provider>
  );
}

export const useStickyBar = () => {
  const context = useContext(StickyBarContext);
  if (!context) {
    throw new Error("useStickyBar must be used inside StickyBarProvider");
  }
  return context;
};

export const useStickyBarSuppression = (suppressed) => {
  const { suppressStickyBar } = useStickyBar();
  useLayoutEffect(() => {
    if (!suppressed) return undefined;
    return suppressStickyBar();
  }, [suppressed, suppressStickyBar]);
};

export const useStickyBarRegistration = (config = null) => {
  const { getStickyBarState, registerStickyBar, unregisterStickyBar } =
    useStickyBar();
  const configRef = useRef(config);
  const id = config?.id || "";
  const signature = getRegistrationSignature(config);

  configRef.current = config;

  useLayoutEffect(() => {
    if (!config?.id) return undefined;
    const registeredId = String(config.id).trim();
    registerStickyBar(buildRegistrationFromRef(configRef));
    return () => unregisterStickyBar(registeredId);
  }, [config?.id, registerStickyBar, unregisterStickyBar]);

  useLayoutEffect(() => {
    if (!config?.id) return;
    registerStickyBar(buildRegistrationFromRef(configRef));
  }, [config?.id, registerStickyBar, signature]);

  return id ? getStickyBarState(id) : null;
};

export function StickyBarHost({ scope = DEFAULT_SCOPE }) {
  const { closeStickyBar, getActiveStickyBar, isSuppressed } = useStickyBar();
  const isDesktopActionViewport = useMediaQuery(DESKTOP_MEDIA_QUERY, {
    noSsr: true,
  });
  const isMobileOverlayViewport = useMediaQuery("(max-width:600px)", {
    noSsr: true,
  });
  const prefersReducedMotion = usePrefersReducedMotion();
  const requestedRegistration = isSuppressed ? null : getActiveStickyBar(scope);
  const [renderedRegistration, setRenderedRegistration] = useState(null);
  const [readyToHide, setReadyToHide] = useState(false);
  const activeRegistration = requestedRegistration ||
    (!readyToHide ? renderedRegistration : null);
  const [measuredHeight, setMeasuredHeight] = useState(0);
  const contentRef = useRef(null);
  const lastVisibleIdRef = useRef("");
  const transitionMs = prefersReducedMotion ? 1 : ANIMATION_MS;
  const layout = renderedRegistration?.layout || activeRegistration?.layout || {};
  const minHeight = layout.minHeight ?? DEFAULT_STICKY_BAR_MIN_HEIGHT;
  const minimumTargetHeight = getCssLengthValuePx(
    minHeight,
    // Responsive heights are enforced by the shell's CSS and measured below.
    // A scalar fallback must not inflate a 40px mobile shell to 44px.
    typeof minHeight === "object" ? 0 : DEFAULT_STICKY_BAR_MIN_HEIGHT,
  );
  const targetHeight = activeRegistration
    ? Math.max(measuredHeight, minimumTargetHeight)
    : 0;

  useLayoutEffect(() => {
    if (requestedRegistration) {
      setReadyToHide(false);
      return undefined;
    }
    if (!renderedRegistration || prefersReducedMotion) {
      setReadyToHide(true);
      return undefined;
    }

    // Let an opening overlay mount and paint before starting the collapse.
    let secondFrame = null;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => setReadyToHide(true));
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame != null) window.cancelAnimationFrame(secondFrame);
    };
  }, [prefersReducedMotion, renderedRegistration, requestedRegistration]);

  useLayoutEffect(() => {
    if (activeRegistration) {
      setRenderedRegistration(activeRegistration);
    }
  }, [activeRegistration]);

  useLayoutEffect(() => {
    const node = contentRef.current;
    if (!node || !renderedRegistration) {
      setMeasuredHeight(0);
      return undefined;
    }

    const measure = () => {
      const rect = node.getBoundingClientRect?.();
      const nextHeight = Math.ceil(
        rect?.height || node.offsetHeight || node.scrollHeight || 0,
      );
      setMeasuredHeight(Math.max(0, nextHeight));
    };

    measure();

    if (typeof ResizeObserver !== "function") {
      const frameId = window.requestAnimationFrame(measure);
      return () => window.cancelAnimationFrame(frameId);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [renderedRegistration]);

  useLayoutEffect(() => {
    const heightCallbackRegistration =
      activeRegistration || renderedRegistration;
    const heightValue = `${Math.max(0, targetHeight)}px`;
    const overlayValue =
      !isSuppressed && activeRegistration?.overlayPolicy ===
      STICKY_BAR_OVERLAY_POLICY.persistentOverlays &&
      !isMobileOverlayViewport
        ? heightValue
        : "0px";

    setRootCssVar(STICKY_BAR_HEIGHT_VAR, heightValue);
    setRootCssVar(STICKY_BAR_TOP_INSET_VAR, heightValue);
    setRootCssVar(STICKY_BAR_OVERLAY_TOP_INSET_VAR, overlayValue);

    heightCallbackRegistration?.onHeightChange?.({
      id: heightCallbackRegistration.id,
      height: Math.max(0, targetHeight),
    });

    return undefined;
  }, [activeRegistration, isMobileOverlayViewport, isSuppressed, renderedRegistration, targetHeight]);

  useEffect(() => resetRootCssVars, []);

  useEffect(() => {
    if (!activeRegistration?.id) return;
    if (lastVisibleIdRef.current === activeRegistration.id) return;
    lastVisibleIdRef.current = activeRegistration.id;
    activeRegistration.onVisible?.({ id: activeRegistration.id });
  }, [activeRegistration]);

  useEffect(() => {
    if (activeRegistration) return;
    lastVisibleIdRef.current = "";
  }, [activeRegistration]);

  useEffect(() => {
    if (activeRegistration || !renderedRegistration) return undefined;
    if (!prefersReducedMotion) return undefined;
    setRenderedRegistration(null);
    return undefined;
  }, [activeRegistration, prefersReducedMotion, renderedRegistration]);

  const handleTransitionEnd = () => {
    if (!activeRegistration && targetHeight === 0) {
      setRenderedRegistration(null);
      setMeasuredHeight(0);
    }
  };

  const handleClose = () => {
    if (!renderedRegistration?.id) return;
    closeStickyBar(renderedRegistration.id, "dismiss");
  };

  const invokeRegistrationAction = (event) => {
    const action = renderedRegistration?.action;
    if (!hasRegistrationAction(renderedRegistration)) return;

    action.onClick?.({
      close: handleClose,
      event,
      id: renderedRegistration.id,
    });

    if (
      event?.defaultPrevented ||
      !action.href ||
      typeof window === "undefined"
    ) {
      return;
    }

    if (action.target === "_blank") {
      window.open(action.href, action.target, "noopener,noreferrer");
      return;
    }

    window.location.assign(action.href);
  };

  const handleBarClick = (event) => {
    if (!hasRegistrationAction(renderedRegistration)) return;
    if (shouldIgnoreBarActionEvent(event)) return;
    invokeRegistrationAction(event);
  };

  const handleActionButtonClick = (event) => {
    event.stopPropagation();

    if (renderedRegistration?.action?.href) {
      renderedRegistration.action.onClick?.({
        close: handleClose,
        event,
        id: renderedRegistration.id,
      });
      return;
    }

    invokeRegistrationAction(event);
  };

  const handleCloseClick = (event) => {
    event.stopPropagation();
    handleClose();
  };

  const leadingGutter = getCssLength(
    layout.leadingGutter ?? layout.startGutter,
    DEFAULT_DISMISSIBLE_LEADING_GUTTER,
  );
  const closeWidth =
    layout.closeWidth ?? layout.closeSize ?? DEFAULT_CLOSE_TARGET_WIDTH;
  const closeHeight = layout.closeHeight ?? layout.closeSize ?? minHeight;
  const closeColumnWidth = getCssLength(
    closeWidth,
    DEFAULT_CLOSE_TARGET_WIDTH,
  );
  const action = renderedRegistration?.action || null;
  const hasAction = hasRegistrationAction(renderedRegistration);
  const showActionForViewport = isDesktopActionViewport
    ? action?.showOnDesktop !== false
    : action?.showOnMobile !== false;
  const showActionButton =
    hasAction &&
    action?.label &&
    action?.showButton !== false &&
    showActionForViewport;
  const hasContentButton = hasAction && action.contentButton === true;
  const shouldRenderKeyboardAction =
    hasAction && !showActionButton && !hasContentButton;
  const actionRel =
    action?.rel ||
    (action?.target === "_blank" ? "noopener noreferrer" : undefined);
  const content =
    renderedRegistration?.renderContent?.({
      action: hasAction
        ? {
            label: action.label,
            onClick: invokeRegistrationAction,
          }
        : null,
      close: handleClose,
      id: renderedRegistration.id,
      isOpen: Boolean(activeRegistration),
    }) || null;

  return (
    <Box
      data-testid="sticky-bar-host"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 2,
        height: `${targetHeight}px`,
        overflow: "hidden",
        bgcolor: layout.bgcolor || "background.paper",
        color: layout.color || "text.primary",
        borderBottom:
          renderedRegistration && targetHeight > 0
            ? (layout.borderBottom ?? "1px solid")
            : "0 solid",
        borderColor: layout.borderColor || "divider",
        boxShadow: layout.boxShadow,
        transition: `height ${transitionMs}ms ease, border-width ${transitionMs}ms ease, background-color 140ms ease`,
        boxSizing: "border-box",
        cursor: hasAction ? "pointer" : "default",
        ...layout.containerSx,
      }}
      onTransitionEnd={handleTransitionEnd}
      onClick={handleBarClick}
    >
      {renderedRegistration ? (
        <Box
          ref={contentRef}
          data-testid="sticky-bar-content"
          sx={{
            width: "100%",
            minHeight: 0,
            boxSizing: "border-box",
          }}
        >
          <Box
            data-testid="sticky-bar-shell"
            sx={{
              display: "grid",
              gridTemplateColumns: renderedRegistration.dismissible
                ? `${leadingGutter} minmax(0, 1fr) ${closeColumnWidth}`
                : "minmax(0, 1fr)",
              alignItems: layout.align || "center",
              width: "100%",
              minHeight,
              boxSizing: "border-box",
              position: "relative",
              ...layout.shellSx,
            }}
          >
            {renderedRegistration.dismissible ? (
              <Box
                aria-hidden="true"
                data-testid="sticky-bar-leading-gutter"
                sx={{ width: leadingGutter, height: minHeight }}
              />
            ) : null}
            {shouldRenderKeyboardAction ? (
              <Button
                aria-label={
                  action.ariaLabel ||
                  action.label ||
                  `Open ${renderedRegistration.id}`
                }
                color={action.color || "primary"}
                data-testid="sticky-bar-keyboard-action"
                onClick={handleActionButtonClick}
                type="button"
                variant={action.variant || "contained"}
                sx={getKeyboardOnlyActionButtonSx(minHeight)}
              >
                {action.label || action.ariaLabel || "Open"}
              </Button>
            ) : null}
            <Box
              data-testid="sticky-bar-main"
              sx={{
                gridColumn: renderedRegistration.dismissible ? "2" : "1",
                display: "flex",
                alignItems: layout.align || "center",
                justifyContent: layout.justify || "center",
                gap: layout.gap ?? 1,
                width: layout.width || "100%",
                maxWidth: layout.maxWidth || "100%",
                minWidth: 0,
                minHeight,
                mx: "auto",
                px: layout.px ?? { xs: 1.5, sm: 2 },
                py: layout.py ?? (layout.density === "comfortable" ? 1.5 : 1),
                boxSizing: "border-box",
                ...layout.contentSx,
              }}
            >
              <Box
                component={hasContentButton ? ButtonBase : "div"}
                aria-label={
                  hasContentButton ? action.ariaLabel || action.label : undefined
                }
                onClick={
                  hasContentButton
                    ? (event) => {
                        event.stopPropagation();
                        invokeRegistrationAction(event);
                      }
                    : undefined
                }
                data-testid="sticky-bar-content-body"
                sx={{
                  minWidth: 0,
                  overflow: shouldConstrainWrappedContent(layout)
                    ? "hidden"
                    : undefined,
                  textOverflow: shouldConstrainWrappedContent(layout)
                    ? "ellipsis"
                    : undefined,
                  ...getContentWrapSx(layout),
                  ...layout.contentBodySx,
                }}
              >
                {content}
              </Box>
              {showActionButton ? (
                <Button
                  {...action.buttonProps}
                  aria-label={action.ariaLabel || action.label}
                  color={action.color || "primary"}
                  component={action.href ? "a" : "button"}
                  data-testid="sticky-bar-action-button"
                  href={action.href || undefined}
                  onClick={handleActionButtonClick}
                  rel={actionRel}
                  target={action.target || undefined}
                  type={action.href ? undefined : "button"}
                  variant={action.variant || "contained"}
                  sx={{
                    minHeight: 44,
                    textTransform: "none",
                    borderRadius: 1,
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                    },
                    ...action.buttonProps?.sx,
                    ...action.sx,
                  }}
                >
                  {action.label}
                </Button>
              ) : null}
            </Box>
            {renderedRegistration.dismissible ? (
              <IconButton
                aria-label={renderedRegistration.closeLabel}
                onClick={handleCloseClick}
                sx={{
                  gridColumn: "3",
                  width: closeWidth,
                  height: closeHeight,
                  borderRadius: 1,
                  justifySelf: "end",
                  flexShrink: 0,
                  ...layout.closeSx,
                }}
              >
                <CloseRounded />
              </IconButton>
            ) : null}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
